import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import Icon, { IconType } from 'components/Icon';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { Tooltip } from 'components/Widget/Tooltip';
import { MenuItem, MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import { showContextMenu } from 'redux/mode/actions';
import type { ChatTab } from 'redux/mode/util/chatTabs';
import * as S from './styles';

interface HeaderProps {
	tabs: ChatTab[];
	activeChatId: string | null;
	chats: Array<{ id: string; title?: string | null }>;
	onSelectChat: (id: string) => void;
	onNewChat: () => void;
	onCloseTab: (id: string) => void;
	onCloseOtherTabs: (id: string) => void;
	onReplaceTabWithNewChat: (id: string) => void;
	// per-chat actions (rename, copy id, ...); empty for a chat with no server row
	buildChatMenuItems: (chatId: string) => MenuItemAction[];
}

export const Header = ({
	tabs,
	activeChatId,
	chats,
	onSelectChat,
	onNewChat,
	onCloseTab,
	onCloseOtherTabs,
	onReplaceTabWithNewChat,
	buildChatMenuItems,
}: HeaderProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [tooltipId] = useState(() => String(Math.random()));

	// The panel always shows a chat, so the last tab has no close affordance.
	const isClosable = tabs.length > 1;

	//------------------------------------------------------------------------------------------------
	// MENUS
	//------------------------------------------------------------------------------------------------

	// Tab actions lead, then the chat's own actions, then the one that changes
	// which chat the tab holds.
	const tabMenuItems = (tab: ChatTab): MenuItem[] => {
		const items: MenuItem[] = [
			new MenuItemAction('Close', '', () => onCloseTab(tab.id), !isClosable),
			new MenuItemAction('Close others', '', () => onCloseOtherTabs(tab.id), !isClosable),
		];
		const chatItems = buildChatMenuItems(tab.id);
		if (chatItems.length > 0) {
			items.push(new MenuItemDivider(), ...chatItems);
		}
		items.push(
			new MenuItemDivider(),
			new MenuItemAction(
				'New chat in this tab',
				'',
				() => onReplaceTabWithNewChat(tab.id),
				tab.isDraft
			)
		);
		return items;
	};

	// The app-wide context menu opens on the bubbling right-click and renders
	// whatever these handlers put in redux, so they must not stop the event.
	const onTabContextMenu = (e: React.MouseEvent, tab: ChatTab) => {
		dispatch(
			showContextMenu({ point: { x: e.clientX, y: e.clientY }, menuItems: tabMenuItems(tab) })
		);
	};

	// The bar itself is about the collection, not any one chat. Tabs are spotted by
	// attribute, since their own handler has already run on this same event.
	const onStripContextMenu = (e: React.MouseEvent) => {
		if (e.target instanceof HTMLElement && e.target.closest('[data-chat-tab]')) return;
		dispatch(
			showContextMenu({
				point: { x: e.clientX, y: e.clientY },
				menuItems: [new MenuItemAction('New chat', '', onNewChat)],
			})
		);
	};

	const historyMenuItems = useMemo(() => {
		if (chats.length === 0) {
			return [new MenuItemAction('No chats available', '', () => {}, true)];
		}

		// Only show checkmark if activeChatId is actually in the chats list
		// (avoids showing checkmark during transitional state when new chat is being created)
		const activeChatExists =
			activeChatId !== null && chats.some((chat) => chat.id === activeChatId);

		return chats.map((chat) => {
			const isActive = activeChatExists && chat.id === activeChatId;
			return new MenuItemAction(
				chat.title || 'Untitled Chat',
				'',
				() => onSelectChat(chat.id),
				false,
				isActive // showCheckmark
			);
		});
	}, [chats, activeChatId, onSelectChat]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<S.Base onContextMenu={onStripContextMenu}>
			<S.TabStrip role="tablist">
				{tabs.map((tab) => {
					const isActive = tab.id === activeChatId;
					return (
						<S.Tab
							key={tab.id}
							data-chat-tab
							$active={isActive}
							role="tab"
							aria-selected={isActive}
							onClick={() => !isActive && onSelectChat(tab.id)}
							onContextMenu={(e) => onTabContextMenu(e, tab)}
							data-tooltip-id={tooltipId}
							data-tooltip-content={tab.title}
						>
							<S.TabLabel>{tab.title}</S.TabLabel>
							{isClosable && (
								<S.TabActions onClick={(e) => e.stopPropagation()}>
									<S.TabActionButton
										role="button"
										tabIndex={0}
										aria-label={`Close ${tab.title}`}
										onClick={() => onCloseTab(tab.id)}
										onKeyDown={(e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault();
												e.stopPropagation();
												onCloseTab(tab.id);
											}
										}}
									>
										<Icon type={IconType.CLOSE} width={10} height={10} />
									</S.TabActionButton>
								</S.TabActions>
							)}
						</S.Tab>
					);
				})}
				<S.AddButton
					onClick={onNewChat}
					data-tooltip-id={tooltipId}
					data-tooltip-content="New chat"
				>
					<Icon type={IconType.ADD} width={16} height={16} color={theme.gray[900]} />
				</S.AddButton>
			</S.TabStrip>
			<S.ButtonGroup>
				<DropdownMenu menuItems={historyMenuItems}>
					<S.DropdownTrigger data-tooltip-id={tooltipId} data-tooltip-content="Chat history">
						<Icon type={IconType.HISTORY} width={16} height={16} color={theme.gray[900]} />
					</S.DropdownTrigger>
				</DropdownMenu>
				<Tooltip id={tooltipId} placement="bottom" />
			</S.ButtonGroup>
		</S.Base>
	);
};
